import {
  Popover as RawPopover,
  PopoverProps as RawPopoverProps,
} from '@mantine/core';
import { ChevronDown } from 'baseui/icon';
import stylesColors from 'common/styles/styles.colors';
import {
  PopoverActionContainer,
  PopoverActionItemButton,
  PopoverActionTrigger,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { StyleObject, StyletronComponent } from 'styletron-react';

import notification from './common/notification';

export type PopoverMenuType = {
  label: string;
  onClick: () => void;
  key: string;
  isError?: boolean;
  props?: StyletronComponent<'div', object>;
  isLoading?: boolean;
  isAllowed?: boolean;
};

export interface PopoverProps extends Omit<RawPopoverProps, 'children'> {
  actions: PopoverMenuType[];
  children?: React.ReactNode;
}

export default function Popover(props: PopoverProps) {
  const { actions, children, ...rest } = props;
  const [isOpen, setIsOpen] = React.useState(false);
  const { t } = useTranslation();
  const target = children ?? (
    <>
      {t('common:action')} <ChevronDown />
    </>
  );

  const errorStyle = {
    color: stylesColors.redDefault,
    ':hover': {
      backgroundColor: stylesColors.redLightest,
    },
    ':active': {
      backgroundColor: stylesColors.redLightest,
    },
  } as StyleObject;

  if (actions.length === 0) return null;

  return (
    <RawPopover
      position="bottom-end"
      zIndex={4}
      radius={8}
      shadow="rgba(0, 0, 0, 0.16) 0px 4px 16px"
      styles={{
        dropdown: {
          padding: 0,
          borderWidth: 0,
        },
      }}
      opened={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      {...rest}
    >
      <RawPopover.Target>
        <PopoverActionTrigger onClick={() => setIsOpen(!isOpen)}>
          {target}
        </PopoverActionTrigger>
      </RawPopover.Target>
      <RawPopover.Dropdown>
        <PopoverActionContainer>
          {actions.map((action) => {
            return (
              <PopoverActionItemButton
                key={action.key}
                $style={action.isError ? errorStyle : undefined}
                onClick={async () => {
                  try {
                    await setIsOpen(false);
                    action.onClick();
                  } catch (e) {
                    notification.error({ message: <>{e.message}</> });
                  }
                }}
              >
                {action.label}
              </PopoverActionItemButton>
            );
          })}
        </PopoverActionContainer>
      </RawPopover.Dropdown>
    </RawPopover>
  );
}
